import { squareShade } from './fen';
import type { Square } from './types';

const PIECE: Record<string, string> = {
  N: 'knight',
  B: 'bishop',
  R: 'rook',
  Q: 'queen',
  K: 'king',
};

/** Sticky matchers tried in order at the current index. */
const CASTLE_LONG = /(?:\d+\s*\.\s*)?O-O-O/y;
const CASTLE_SHORT = /(?:\d+\s*\.\s*)?O-O/y;
const PIECE_SAN =
  /(?:\d+\s*\.\s*)?(?:…|\.{2,3})?([NBRQK])([a-h]|[1-8])?(x?)([a-h][1-8])(?:=([NBRQK]))?([+#])?/y;
const PAWN_CAPTURE =
  /(?:\d+\s*\.\s*)?(?:…|\.{2,3})?([a-h])x([a-h][1-8])(?:=([NBRQK]))?([+#])?/y;
const SQUARE_PIECE =
  /([a-h][1-8])\s+(bishops?|knights?|rooks?|queens?|kings?|pawns?)\b/y;
const SLASH_SQUARES = /([a-h][1-8])\s*\/\s*([a-h][1-8])/y;
const DASH_SQUARES = /([a-h][1-8])\s*[–—-]\s*([a-h][1-8])/y;
const BARE_SQUARE = /(?:\d+\s*\.\s*)?(?:…|\.{2,3})?([a-h][1-8])([+#])?/y;

const LOCATION_BEFORE =
  /(?:^|[^A-Za-z])(?:to|towards?|onto|into|via|from|on|at|of|off|near|around|over|under|between|behind|across|through|take[ns]?|taking|took|captures?|forks?|forking|hits?|hitting|watch(?:es|ing)?|guards?|guarding|defends?|defending|grabs?|occup(?:y|ies|ying)|controls?|controlling)\s+$/i;

const PAWN_LIST_BEFORE =
  /pawn to (?:light|dark) [a-h][1-8],?\s+(?:(?:and|or)\s+)?$/i;

const LOC_LIST_BEFORE =
  /(?:light|dark) [a-h][1-8],?\s+(?:(?:and|or)\s+)?$/i;

const LOCATION_AFTER =
  /^\s+(?:(?:often|still|already|also|now|just|then|never|always)\s+)?(?:(?:is|are|was|were)\s+)?(?:hangs|hanging|falls|fell|soft|weak|loose|unguarded|undefended)\b/i;

const NEW_CLAUSE_AFTER =
  /^\s*(?:,\s*)?(?:is|are|was|were|can|could|will|would)\b/i;

function isWordChar(ch: string | undefined): boolean {
  return ch !== undefined && /[A-Za-z0-9]/.test(ch);
}

function matchSticky(re: RegExp, text: string, i: number): RegExpExecArray | null {
  re.lastIndex = i;
  const m = re.exec(text);
  return m && m.index === i ? m : null;
}

function shadePhrase(square: string): string {
  const shade = squareShade(square as Square);
  return `${shade} ${square}`;
}

function withCheck(phrase: string, chk: string | undefined): string {
  if (chk === '+') return `${phrase}, check`;
  if (chk === '#') return `${phrase}, mate`;
  return phrase;
}

function withPromo(phrase: string, promo: string | undefined): string {
  if (!promo) return phrase;
  const name = PIECE[promo] ?? promo;
  return `${phrase}, promoting to ${name}`;
}

function shouldCapitalize(out: string): boolean {
  const t = out.replace(/[\s…«»“”"'(\[]+$/u, '');
  return t.length === 0 || /[.!?]$/.test(t);
}

function cap(phrase: string, yes: boolean): string {
  if (!yes || phrase.length === 0) return phrase;
  return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}

function glue(phrase: string, after: string): string {
  if (!/^\s*[A-Za-z]/.test(after)) return phrase;
  if (/, (?:check|mate)$/.test(phrase)) return phrase;
  if (/[a-h][1-8]$/.test(phrase)) return phrase;
  return `${phrase},`;
}

function afterOf(text: string, i: number, matched: string): string {
  return text.slice(i + matched.length);
}

function pawnTo(square: string, chk?: string, promo?: string): string {
  return withCheck(withPromo(`pawn to ${shadePhrase(square)}`, promo), chk);
}

function piecePhrase(
  letter: string,
  capture: string,
  square: string,
  promo?: string,
  chk?: string,
): string {
  const name = PIECE[letter] ?? letter;
  const verb = capture === 'x' ? 'takes' : 'to';
  return withCheck(withPromo(`${name} ${verb} ${shadePhrase(square)}`, promo), chk);
}

function isListContinuation(before: string, after: string, pawn: boolean): boolean {
  const re = pawn ? PAWN_LIST_BEFORE : LOC_LIST_BEFORE;
  if (!re.test(before)) return false;
  if (NEW_CLAUSE_AFTER.test(after)) return false;
  return true;
}

function isLocationContext(before: string, after: string): boolean {
  if (isListContinuation(before, after, true)) return false;
  if (LOCATION_BEFORE.test(before)) return true;
  if (isListContinuation(before, after, false)) return true;
  if (LOCATION_AFTER.test(after)) return true;
  return false;
}

/**
 * Spell chess notation as English, including square color.
 * `e4` → "pawn to light e4"; `Nf6` → "knight to dark f6".
 * Squares already after to/on/at/takes keep the preposition and only add color.
 */
export function notationToWords(text: string): string {
  let out = '';
  let i = 0;

  while (i < text.length) {
    if (isWordChar(text[i]) && isWordChar(text[i - 1])) {
      out += text[i];
      i += 1;
      continue;
    }

    const castleLong = matchSticky(CASTLE_LONG, text, i);
    if (castleLong) {
      const after = afterOf(text, i, castleLong[0]);
      out += glue(cap('queenside castle', shouldCapitalize(out)), after);
      i += castleLong[0].length;
      continue;
    }

    const castleShort = matchSticky(CASTLE_SHORT, text, i);
    if (castleShort) {
      const after = afterOf(text, i, castleShort[0]);
      out += glue(cap('kingside castle', shouldCapitalize(out)), after);
      i += castleShort[0].length;
      continue;
    }

    const piece = matchSticky(PIECE_SAN, text, i);
    if (piece) {
      const after = afterOf(text, i, piece[0]);
      const phrase = piecePhrase(piece[1]!, piece[3] ?? '', piece[4]!, piece[5], piece[6]);
      out += glue(cap(phrase, shouldCapitalize(out)), after);
      i += piece[0].length;
      continue;
    }

    const px = matchSticky(PAWN_CAPTURE, text, i);
    if (px) {
      const after = afterOf(text, i, px[0]);
      const phrase = withCheck(withPromo(`pawn takes ${shadePhrase(px[2]!)}`, px[3]), px[4]);
      out += glue(cap(phrase, shouldCapitalize(out)), after);
      i += px[0].length;
      continue;
    }

    const sqPiece = matchSticky(SQUARE_PIECE, text, i);
    if (sqPiece) {
      const after = afterOf(text, i, sqPiece[0]);
      const phrase = `${sqPiece[2]!} on ${shadePhrase(sqPiece[1]!)}`;
      out += glue(cap(phrase, shouldCapitalize(out)), after);
      i += sqPiece[0].length;
      continue;
    }

    const slash = matchSticky(SLASH_SQUARES, text, i);
    if (slash) {
      const after = afterOf(text, i, slash[0]);
      out += glue(`${shadePhrase(slash[1]!)} and ${shadePhrase(slash[2]!)}`, after);
      i += slash[0].length;
      continue;
    }

    const dash = matchSticky(DASH_SQUARES, text, i);
    if (dash) {
      const after = afterOf(text, i, dash[0]);
      if (isLocationContext(out, after)) {
        out += glue(`${shadePhrase(dash[1]!)} to ${shadePhrase(dash[2]!)}`, after);
      } else {
        const first = pawnTo(dash[1]!);
        out += glue(
          cap(`${first}, then ${pawnTo(dash[2]!)}`, shouldCapitalize(out)),
          after,
        );
      }
      i += dash[0].length;
      continue;
    }

    const bare = matchSticky(BARE_SQUARE, text, i);
    if (bare) {
      const square = bare[1]!;
      const chk = bare[2];
      const after = afterOf(text, i, bare[0]);
      if (isLocationContext(out, after)) {
        out += glue(withCheck(shadePhrase(square), chk), after);
      } else {
        out += glue(cap(pawnTo(square, chk), shouldCapitalize(out)), after);
      }
      i += bare[0].length;
      continue;
    }

    out += text[i];
    i += 1;
  }

  return out;
}
